import { apiHelpers } from '../utils/api';
import type {
  RentalContract,
  RentalContractsResponse,
  CreateRentalContractData,
  UpdateRentalContractData,
  ReturnMachinesData,
  GetRentalContractsQuery,
  RentalDashboardStats,
  RentedMachineInfo,
  ExpiringContract,
  OilInventoryStatus,
  RevenueReportResponse,
  CreateVisitScheduleData,
  UpdateVisitStatusData,
  ScheduledVisitResponse,
  UpcomingVisitsResponse,
  VisitsThisWeekResponse,
  VisitType,
  SelectiveReturnData,
  AvailableMachineForReplacement
} from '../types/rental';

export const rentalService = {
  // ========== Rental Contracts ==========

  // Get all rental contracts with filters
  getRentalContracts: async (query?: GetRentalContractsQuery): Promise<RentalContractsResponse> => {
    const params = new URLSearchParams();

    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.status) params.append('status', query.status);
    if (query?.customerId) params.append('customerId', query.customerId);
    if (query?.startDate) params.append('startDate', query.startDate);
    if (query?.endDate) params.append('endDate', query.endDate);
    if (query?.search) params.append('search', query.search);

    return await apiHelpers.get(`/rentals?${params.toString()}`);
  },

  // Get single rental contract by ID
  getRentalContract: async (id: string): Promise<RentalContract> => {
    return await apiHelpers.get(`/rentals/${id}`);
  },

  // Create new rental contract
  createRentalContract: async (data: CreateRentalContractData): Promise<RentalContract> => {
    return await apiHelpers.post('/rentals', data);
  },

  // Update rental contract
  updateRentalContract: async (id: string, data: UpdateRentalContractData): Promise<RentalContract> => {
    return await apiHelpers.put(`/rentals/${id}`, data);
  },

  // Renew rental contract
  renewRentalContract: async (id: string, data: { endDate: Date | string }): Promise<RentalContract> => {
    return await apiHelpers.patch(`/rentals/${id}/renew`, data);
  },

  // Terminate rental contract
  terminateRentalContract: async (id: string, data: { reason?: string }): Promise<RentalContract> => {
    return await apiHelpers.patch(`/rentals/${id}/terminate`, data);
  },

  // Return machines from a rental contract
  returnMachines: async (id: string, data: ReturnMachinesData): Promise<RentalContract> => {
    return await apiHelpers.post(`/rentals/${id}/return-machines`, data);
  },

  // Suspend rental contract (change to INACTIVE status)
  suspendContract: async (id: string): Promise<{ success: boolean; data: RentalContract; message: string }> => {
    return await apiHelpers.patch(`/rentals/${id}/suspend`, {});
  },

  // Return all machines at once
  returnAllMachines: async (id: string, data: {
    returnDate: Date | string;
    returnCondition: string;
    notes?: string;
    setContractStatus?: string;
  }): Promise<{ success: boolean; data: RentalContract; returnedCount: number; message: string }> => {
    return await apiHelpers.post(`/rentals/${id}/return-all-machines`, data);
  },

  // Selective return and replace machines
  selectiveReturnAndReplace: async (
    id: string,
    data: SelectiveReturnData
  ): Promise<{
    success: boolean;
    data: RentalContract;
    returnedCount: number;
    replacedCount: number;
    message: string
  }> => {
    return await apiHelpers.post(`/rentals/${id}/selective-return-replace`, data);
  },

  // Get available machines by product ID for replacement
  getAvailableMachinesByProduct: async (
    productId: string
  ): Promise<{
    success: boolean;
    data: AvailableMachineForReplacement[];
    count: number;
  }> => {
    return await apiHelpers.get(`/rentals/available-machines/${productId}`);
  },

  // Delete rental contract
  deleteRentalContract: async (id: string): Promise<void> => {
    return await apiHelpers.delete(`/rentals/${id}`);
  },

  // ========== Dashboard & Analytics ==========

  // Get rental dashboard statistics
  getDashboardStats: async (): Promise<{ success: boolean; data: RentalDashboardStats }> => {
    return await apiHelpers.get('/rental-dashboard/stats');
  },

  // Get list of all rented machines
  getRentedMachines: async (query?: { customerId?: string; status?: string }): Promise<{ success: boolean; data: RentedMachineInfo[] }> => {
    const params = new URLSearchParams();

    if (query?.customerId) params.append('customerId', query.customerId);
    if (query?.status) params.append('status', query.status);

    return await apiHelpers.get(`/rental-dashboard/rented-machines?${params.toString()}`);
  },

  // Get contracts expiring soon
  getExpiringContracts: async (daysAhead: number = 30): Promise<{ success: boolean; data: ExpiringContract[] }> => {
    return await apiHelpers.get(`/rental-dashboard/expiring-contracts?daysAhead=${daysAhead}`);
  },

  // Get oil inventory status
  getOilInventoryStatus: async (): Promise<{ success: boolean; data: OilInventoryStatus[] }> => {
    return await apiHelpers.get('/rental-dashboard/oil-inventory');
  },

  // Get revenue report
  getRevenueReport: async (startDate?: string, endDate?: string): Promise<RevenueReportResponse> => {
    const params = new URLSearchParams();

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return await apiHelpers.get(`/rental-dashboard/revenue-report?${params.toString()}`);
  },

  // ========== Visit Scheduling ==========

  // Schedule a new visit
  scheduleVisit: async (data: CreateVisitScheduleData): Promise<ScheduledVisitResponse> => {
    return await apiHelpers.post('/visits/schedule', data);
  },

  // Get upcoming visits for the next N days
  getUpcomingVisits: async (days: number = 7): Promise<UpcomingVisitsResponse> => {
    return await apiHelpers.get(`/visits/upcoming?days=${days}`);
  },

  // Get visits this week count (for dashboard)
  getVisitsThisWeekCount: async (): Promise<VisitsThisWeekResponse> => {
    return await apiHelpers.get('/visits/this-week/count');
  },

  // Get visits by type
  getVisitsByType: async (type: VisitType): Promise<UpcomingVisitsResponse> => {
    return await apiHelpers.get(`/visits/by-type?type=${type}`);
  },

  // Update visit status
  updateVisitStatus: async (id: string, data: UpdateVisitStatusData): Promise<ScheduledVisitResponse> => {
    return await apiHelpers.patch(`/visits/${id}/status`, data);
  },

  // Delete all SERVICE type visits
  deleteAllServiceVisits: async (): Promise<{ success: boolean; message: string; count: number }> => {
    return await apiHelpers.delete('/visits/service');
  },

  // Get current scent for a contract
  getCurrentScent: async (contractId: string): Promise<{ success: boolean; data: { currentScent: string | null; source: string; lastUpdated: string | null } }> => {
    return await apiHelpers.get(`/visits/contracts/${contractId}/current-scent`);
  }
};
